import { ClientOnly } from "@tanstack/react-router";
import { AwardIcon } from "lucide-react";

import { SignInButton } from "@/web/auth/SignInButton";
import { cn } from "@/web/lib/styling";
import { NominateButton } from "@/web/routes/-nomination/NominateButton";
import { env } from "@/web/lib/env";

type Polaroid = {
  src: string;
  alt: string;
  /** Tailwind rotation utility for the scattered-photo look. */
  rotate: string;
};

const POLAROIDS: Polaroid[] = [
  {
    src: `${env.VITE_WITHCO_IMAGE_CDN_BASE}/tooradeli.jpg`,
    alt: "A local storefront",
    rotate: "-rotate-6",
  },
  {
    src: `${env.VITE_WITHCO_IMAGE_CDN_BASE}/man-woman-storefront.jpg`,
    alt: "Small business owners",
    rotate: "rotate-0",
  },
  {
    src: `${env.VITE_WITHCO_IMAGE_CDN_BASE}/walk-in-paris.jpg`,
    alt: "A neighborhood shopfront",
    rotate: "rotate-3",
  },
];

/**
 * Closing call-to-action: the "Are you with us?" headline above a scatter of
 * storefront polaroids, with the nominate + signup CTAs. Both buttons reuse the
 * existing auth/nominate dialog flows.
 */
export function MarketingCTA() {
  return (
    <section
      data-nav-theme="white"
      className="sticky top-0 flex min-h-dvh w-full items-center bg-withus-white px-6 py-20 md:px-12 md:py-28"
    >
      <div className="mx-auto flex w-full max-w-300 flex-col items-center gap-6">
        <h2 className="withus-strong text-center text-3xl text-withus-red md:text-5xl">
          Are you with us?
        </h2>

        <p className="mb-3 max-w-2xl text-center text-lg md:text-xl">
          The places we love should not have to leave their future in someone else’s hands. Own your
          building or nominate a local business that should.
        </p>

        <div className="mb-6 flex flex-wrap items-center justify-center gap-6">
          {POLAROIDS.map((photo) => (
            <img
              key={photo.src}
              src={photo.src}
              alt={photo.alt}
              className={cn(
                "size-56 border-4 border-withus-pure-white object-cover shadow-withus-default",
                photo.rotate,
              )}
            />
          ))}
        </div>

        {/* Auth-dependent (Clerk) — client-only so the rest of the section SSRs. */}
        <ClientOnly fallback={<div className="h-13" />}>
          <div className="flex flex-col items-center justify-center gap-2 px-4 md:flex-row md:flex-nowrap md:gap-5 md:px-0">
            <NominateButton
              label="Nominate a business"
              icon={AwardIcon}
              size="lg"
              color="blue"
              className="h-12 w-full md:w-auto"
            />
            <SignInButton
              variant="solid"
              color="yellow"
              size="lg"
              className="font-withus-sans h-12 w-full rounded-none md:w-auto"
              label="Own your building"
            />
          </div>
        </ClientOnly>
      </div>
    </section>
  );
}
